import {
  Formik,
  Form as FormikForm,
  FormikHelpers,
  FormikProps,
  ErrorMessage,
  Field,
} from "formik";
import React, { useEffect, useRef, useState } from "react";
import {
  Form as BootstrapForm,
  Button,
  Col,
  Row,
  Spinner,
} from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import PopoverBtn from "../../../../components/inputs/popoverBtn";
import { CustomToast } from "../../../../components/inputs/toast";
import * as Yup from "yup";
import {
  DomainStatusByApiQuery,
  useSetDomainUrlMutation,
} from "../../../../graphql/projects.hooks";
import { DomainType } from "../../../../constants";

interface ISubdomainForm {
  subdomain: string;
}

interface ISubdomainProps {
  handleNextStepDisabled: (value: boolean) => void;
  domainId: number;
  domainStatusData: DomainStatusByApiQuery;
  refetchStatusData: () => void;
}

export default function Subdomain({
  handleNextStepDisabled,
  domainId,
  domainStatusData,
  refetchStatusData,
}: ISubdomainProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<ISubdomainForm>>(null);
  const [isShowAlert, setIsShowAlert] = useState(false);
  const [saveBtnTitle, setSaveBtnTitle] = useState(t("projects.Save"));

  const [setDomain, { loading: setDomainUrlLoading }] =
    useSetDomainUrlMutation();

  const validationSudomain = Yup.object().shape({
    subdomain: Yup.string()
      .required(t("validation.required"))
      .min(1, t("validation.short"))
      .max(255, t("validation.long")),
  });

  useEffect(() => {
    handleNextStepDisabled(true);
  }, [handleNextStepDisabled]);

  const onSubmit = async (
    values: ISubdomainForm,
    { setSubmitting }: FormikHelpers<ISubdomainForm>
  ) => {
    try {
      await setDomain({
        variables: {
          domainId,
          urlType: DomainType.Subdomain,
          domainUrl: values.subdomain,
        },
        onCompleted: () => {
          setIsShowAlert(true);
          handleNextStepDisabled(false);
          setSaveBtnTitle(t("projects.Change"));
          refetchStatusData();
        },
      });
    } catch (error) {}
    setSubmitting(false);
  };

  const initialValues: ISubdomainForm = {
    subdomain:
      (domainStatusData?.domainStatusByApi?.urlType === DomainType.Subdomain &&
        domainStatusData?.domainStatusByApi?.domainUrl?.replace(
          ".me-page.com",
          ""
        )) ||
      "",
  };

  useEffect(() => {
    if (domainStatusData?.domainStatusByApi?.urlType === DomainType.Subdomain) {
      handleNextStepDisabled(false);
      setSaveBtnTitle(t("projects.Change"));
    }
  }, [domainStatusData, handleNextStepDisabled, t]);

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSudomain}
        enableReinitialize
      >
        {({ touched, errors, values }) => {
          return (
            <FormikForm>
              <Row className="g-3 mb-4">
                <Col xs={9} sm={9}>
                  <BootstrapForm.Group
                    controlId="formSubdomain"
                    className="w-100 position-relative overflow-hidden"
                  >
                    <span
                      className="input-content position-absolute top-50 translate-middle-y pe-none text-nowrap"
                      style={{ zIndex: 1, left: 13 }}
                    >
                      <span className="fs-7 opacity-0">{values.subdomain}</span>
                      <span className="text-gray">.me-page.com</span>
                    </span>

                    <Field
                      as={BootstrapForm.Control}
                      type="text"
                      name="subdomain"
                      className={`py-3 subdomain-input bg-transparent position-relative ${touched.subdomain && errors.subdomain ? "is-invalid" : ""}`}
                      style={{ zIndex: 2, backgroundImage: "none" }}
                      autoComplete="off"
                    />

                    <div
                      className="position-absolute top-0 end-0 mt-3 w-auto me-3"
                      style={{ zIndex: 2 }}
                    >
                      <PopoverBtn
                        text={t("projects.It’s your default subdomain name")}
                        icon={<InfoCircle />}
                      />
                    </div>
                  </BootstrapForm.Group>
                  <ErrorMessage
                    name="subdomain"
                    render={(msg) => (
                      <div className="invalid-feedback text-start d-block">
                        {msg}
                      </div>
                    )}
                  />
                </Col>
                <Col xs={3} sm={3}>
                  <Button
                    variant="secondary"
                    className="fw-bold py-3 w-100 text-white text-nowrap"
                    type="submit"
                    disabled={setDomainUrlLoading}
                  >
                    {saveBtnTitle} {setDomainUrlLoading && <Spinner />}
                  </Button>
                </Col>
              </Row>
            </FormikForm>
          );
        }}
      </Formik>
      <div
        className="position-fixed bottom-0 start-50 translate-middle-x"
        style={{ zIndex: 2 }}
      >
        <CustomToast
          isShow={isShowAlert}
          setShow={setIsShowAlert}
          message={`${t("projects.Subdomain successfully changed")}`}
        />
      </div>
    </>
  );
}
